import React, { useState, useRef, useEffect } from 'react';
import { GoogleDocsCaretDown, GoogleDocsArrowRight, CheckmarkIcon } from './editorIcons';

const HEADING_STYLES = [
  {
    id: 'p',
    label: 'Normal text',
    style: { fontSize: '15px', fontWeight: 400, color: '#202124' },
  },
  {
    id: 'title',
    label: 'Title',
    style: { fontSize: '26px', fontWeight: 500, color: '#000000', lineHeight: '1.2' },
  },
  {
    id: 'subtitle',
    label: 'Subtitle',
    style: { fontSize: '15px', fontWeight: 400, color: '#5f6368' },
  },
  {
    id: 'h1',
    label: 'Heading 1',
    style: { fontSize: '20px', fontWeight: 700, color: '#000000' },
  },
  {
    id: 'h2',
    label: 'Heading 2',
    style: { fontSize: '16px', fontWeight: 700, color: '#000000' },
  },
  {
    id: 'h3',
    label: 'Heading 3',
    style: { fontSize: '14px', fontWeight: 700, color: '#434343' },
  },
];

const HeadingDropdown = ({
  value = 'p',
  onChange,
  onSaveSelection,
  onRestoreSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setActiveSubmenu(null);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  // Find active label for the toolbar button
  const currentItem = HEADING_STYLES.find((item) => item.id === value) || HEADING_STYLES[0];

  const handleToggle = (e) => {
    e.preventDefault();
    if (!isOpen && onSaveSelection) {
      onSaveSelection();
    }
    setIsOpen((prev) => !prev);
    setActiveSubmenu(null);
  };

  const handleSelectStyle = (id, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onRestoreSelection) {
      onRestoreSelection();
    }
    if (onChange) {
      onChange(id);
    }
    setIsOpen(false);
    setActiveSubmenu(null);
  };

  const handleResetStyles = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onRestoreSelection) {
      onRestoreSelection();
    }
    if (onChange) {
      onChange('p');
    }
    setIsOpen(false);
    setActiveSubmenu(null);
  };

  return (
    <div className="rte-gdocs-dropdown-container" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        className={`rte-gdocs-toolbar-btn rte-gdocs-heading-trigger ${isOpen ? 'active' : ''}`}
        onMouseDown={(e) => {
          if (onSaveSelection) onSaveSelection();
        }}
        onClick={handleToggle}
        title="Heading and paragraph styles"
      >
        <span className="rte-gdocs-heading-btn-text">{currentItem.label}</span>
        <span className="rte-gdocs-arrow-icon">
          <GoogleDocsCaretDown />
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="rte-gdocs-dropdown-menu rte-gdocs-heading-menu"
          onMouseDown={(e) => e.preventDefault()}
        >
          {HEADING_STYLES.map((item) => {
            const isSelected = value === item.id;
            const isSubmenuOpen = activeSubmenu === item.id;

            return (
              <div
                key={item.id}
                className={`rte-gdocs-heading-item-row ${isSelected ? 'selected' : ''}`}
                onMouseEnter={() => setActiveSubmenu(item.id)}
                onClick={(e) => handleSelectStyle(item.id, e)}
              >
                {/* Left checkmark column */}
                <div className="rte-gdocs-item-check-col">
                  {isSelected && <CheckmarkIcon className="rte-gdocs-check-icon" />}
                </div>

                {/* Styled text column */}
                <div className="rte-gdocs-item-label-col" style={item.style}>
                  {item.label}
                </div>

                {/* Right arrow submenu trigger */}
                <div
                  className="rte-gdocs-item-arrow-col"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSubmenu(activeSubmenu === item.id ? null : item.id);
                  }}
                >
                  <GoogleDocsArrowRight />
                </div>

                {/* Submenu Flyout */}
                {isSubmenuOpen && (
                  <div
                    className="rte-gdocs-flyout-submenu"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="rte-gdocs-flyout-item"
                      onClick={(e) => handleSelectStyle(item.id, e)}
                    >
                      Apply '{item.label}'
                    </div>
                    <div
                      className="rte-gdocs-flyout-item"
                      onClick={(e) => handleSelectStyle(item.id, e)}
                    >
                      Update '{item.label}' to match
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Options Row */}
          <div
            className="rte-gdocs-heading-item-row rte-gdocs-options-row"
            onMouseEnter={() => setActiveSubmenu('options')}
            onClick={(e) => {
              e.stopPropagation();
              setActiveSubmenu(activeSubmenu === 'options' ? null : 'options');
            }}
          >
            <div className="rte-gdocs-item-check-col" />
            <div className="rte-gdocs-item-label-col" style={{ fontSize: '14px', fontWeight: 400, color: '#202124' }}>
              Options
            </div>
            <div className="rte-gdocs-item-arrow-col">
              <GoogleDocsArrowRight />
            </div>

            {/* Options Submenu */}
            {activeSubmenu === 'options' && (
              <div
                className="rte-gdocs-flyout-submenu"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="rte-gdocs-flyout-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                    setActiveSubmenu(null);
                  }}
                >
                  Save as my default styles
                </div>
                <div
                  className="rte-gdocs-flyout-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                    setActiveSubmenu(null);
                  }}
                >
                  Use my default styles
                </div>
                <div
                  className="rte-gdocs-flyout-item"
                  onClick={handleResetStyles}
                >
                  Reset styles
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadingDropdown;
